import { BrainProvisioningStatus } from "@/components/BrainProvisioningStatus";
import { SetupChecklist } from "@/components/SetupChecklist";
import { useSetupConditions } from "@/hooks/useSetupConditions";
import { InlineError } from "@/components/QueryState";
import { api } from "@/lib/api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useConnectReturn } from "@/hooks/useConnectReturn";
import { useCatalogActivity } from "./catalog-activity";
import { CosInboxPitch } from "./cos/CosInboxPitch";
import { ProvisioningHeader, ReadyHeader } from "./cos/pieces";
import { usePreview } from "./preview-context";
import {
  ConnectLater,
  ProvisioningPitch,
  ProvisioningReady,
} from "./ConnectAppsInline";

// Provisioning waiting room. Polls /api/onboarding/state every 2s while
// the backend says we're still here; auto-advances to the connect step
// the moment the backend flips to `step: "connect"`. The user sees an
// animated Boo and its setup checklist (SetupChecklist) plus
// the connect-3-integrations pitch; the catalog itself fills the layout's
// right pane (OnboardingCatalogPane via StepPreview) during this step.
export function ProvisioningStep() {
  useDocumentTitle("Setting up");
  const navigate = useNavigate();

  const stateQuery = useQuery({
    queryKey: ["onboarding-state"],
    queryFn: api.onboarding.state,
    refetchInterval: (q) =>
      q.state.data?.step === "provisioning" ? 2000 : false,
  });

  const data = stateQuery.data;
  const { engaged: engagedNow, connecting } = useCatalogActivity();
  // Back from a hub connect redirect: the user was working the catalog, so
  // this step stays put (the SPA reloaded and lost the in-memory flag).
  const returned = useConnectReturn() !== null;
  const engaged = engagedNow || returned;
  const ready = data?.step === "connect";
  const { posture } = usePreview();
  const isCos = posture === "chief-of-staff";

  // Handoff rule: auto-advance to the connect step only if the user never
  // touched the catalog and no connect flow is mid-flight. An engaged user
  // keeps this page (the spinner swaps to a ready state with a Continue
  // button) so their browsing isn't yanked away; the consent overlay lives
  // outside React and would survive a swap, but the page under it
  // shouldn't change. GatedShell tolerates sitting here while the backend
  // says "connect" for exactly this reason.
  useEffect(() => {
    if (!ready) return;
    // Chief of staff: connecting the inbox is the point of this screen, so
    // the ready state waits for an explicit Continue instead of advancing.
    if (isCos) return;
    if (engaged || connecting > 0) return;
    // search: {} clears the OAuth-callback params the connect route now
    // preserves via validateSearch (BOO-309) when we arrive from here.
    navigate({ to: "/onboarding/connect", search: {}, replace: true });
  }, [ready, engaged, connecting, navigate, isCos]);

  const botName = data?.containerName?.trim() || "Boo";
  // The Boo being set up: connections made on this screen are private to it.
  const containerId = data?.containerId;
  const conditions = useSetupConditions(containerId, !ready && data?.containerStatus !== "error");

  if (stateQuery.isError) {
    return <InlineError error={stateQuery.error ?? "Failed to load onboarding state"} />;
  }

  // If the container's deploy itself failed, surface the error instead
  // of spinning forever. Domain `error` is non-fatal — the cert just
  // didn't issue; the user can still connect via the Railway URL.
  if (data?.containerStatus === "error") {
    return (
      <InlineError
        error={data.containerError ?? "Provisioning failed"}
      />
    );
  }

  if (isCos) {
    return (
      <div className="flex flex-col gap-6">
        {ready ? (
          <ReadyHeader booName={botName} />
        ) : (
          <>
            <ProvisioningHeader booName={botName} />
            <SetupChecklist conditions={conditions} />
          </>
        )}
        <CosInboxPitch
          booName={botName}
          containerId={containerId}
          ready={ready}
          onContinue={() => navigate({ to: "/onboarding/connect", search: {} })}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {ready ? (
        <ProvisioningReady botName={botName} />
      ) : (
        <BrainProvisioningStatus botName={botName} conditions={conditions} />
      )}
      <ProvisioningPitch botName={botName} containerId={containerId} />
      {/* The only way forward sits last, below the value pitch, as a quiet
          link — not a competing CTA. */}
      {ready && (
        <ConnectLater
          botName={botName}
          containerId={containerId}
          onContinue={() => navigate({ to: "/onboarding/connect", search: {} })}
        />
      )}
    </div>
  );
}
